/**
 * RTWO Cryptography & Identity Engine — Provider Registration Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { RTWOIdentityBackendDescriptor } from '../adapters/identity-backend-interface';
import type { IIdentityBackend } from '../adapters/identity-backend-interface';
import type { IKeyProvider, RTWOKeyProviderDescriptor } from '../adapters/key-provider-interface';
import type { IProviderRegistrationService } from '../service-interfaces';
import type { IdentityBackendRegistry, KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class ProviderRegistrationService
  extends BaseCryptoService
  implements IProviderRegistrationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.PROVIDER_REGISTRATION;
  readonly serviceName = 'Provider Registration Service';
  readonly description = 'Registers key provider and identity backend implementations.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R6 Security & Automation', 'Integration Framework'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly keyProviderRegistry: KeyProviderRegistry,
    private readonly identityBackendRegistry: IdentityBackendRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'ready';
  }

  async registerKeyProvider(provider: IKeyProvider): Promise<boolean> {
    if (provider.providerType === 'not_configured') {
      return false;
    }

    this.keyProviderRegistry.register(provider);
    this.context.eventBus.emit(CryptoEventTypes.KEY_PROVIDER_REGISTERED, provider.providerId, {
      providerId: provider.providerId,
      providerType: provider.providerType,
    });
    return true;
  }

  async unregisterKeyProvider(providerId: string): Promise<boolean> {
    return this.keyProviderRegistry.unregister(providerId);
  }

  async registerIdentityBackend(backend: IIdentityBackend): Promise<boolean> {
    if (backend.backendType === 'not_configured') {
      return false;
    }

    this.identityBackendRegistry.register(backend);
    this.context.eventBus.emit(CryptoEventTypes.IDENTITY_BACKEND_REGISTERED, backend.backendId, {
      backendId: backend.backendId,
      backendType: backend.backendType,
    });
    return true;
  }

  async unregisterIdentityBackend(backendId: string): Promise<boolean> {
    return this.identityBackendRegistry.unregister(backendId);
  }

  getRegisteredKeyProviders(): RTWOKeyProviderDescriptor[] {
    return this.keyProviderRegistry.getAllProviders().map((provider) => provider.getDescriptor());
  }

  getRegisteredIdentityBackends(): RTWOIdentityBackendDescriptor[] {
    return this.identityBackendRegistry.getAllBackends().map((backend) => backend.getDescriptor());
  }

  getActiveKeyProviderId(): string | null {
    const provider = this.keyProviderRegistry.getActiveProvider();
    return this.keyProviderRegistry.isConfigured() ? provider.providerId : null;
  }

  getActiveIdentityBackendId(): string | null {
    const backend = this.identityBackendRegistry.getActiveBackend();
    return this.identityBackendRegistry.isConfigured() ? backend.backendId : null;
  }

  isKeyProviderConfigured(): boolean {
    return this.keyProviderRegistry.isConfigured();
  }

  isIdentityBackendConfigured(): boolean {
    return this.identityBackendRegistry.isConfigured();
  }
}
